import { BehaviorSubject } from 'rxjs';
import {
  ManagedApp,
  TenantStatus,
  UseCaseApp,
  USE_CASE_APP_METADATA,
  DeploymentState
} from '@services/app-management/app-management.model';

export type AppManagementMockMode = 'no-use-cases' | 'all-use-cases';

const APP_MANAGEMENT_MODE_KEY = 'htx-mock-app-management-mode';

function getInitialMode(): AppManagementMockMode {
  try {
    const stored = localStorage.getItem(APP_MANAGEMENT_MODE_KEY);
    if (stored === 'no-use-cases') return 'no-use-cases';
  } catch {
    // localStorage not available
  }
  return 'all-use-cases';
}

const mockMode$ = new BehaviorSubject<AppManagementMockMode>(getInitialMode());

export function setAppManagementMockMode(mode: AppManagementMockMode): void {
  mockMode$.next(mode);
  try {
    localStorage.setItem(APP_MANAGEMENT_MODE_KEY, mode);
  } catch {
    // localStorage not available
  }
}

export function getAppManagementMockMode$() {
  return mockMode$.asObservable();
}

export function getCurrentAppManagementMockMode(): AppManagementMockMode {
  return mockMode$.value;
}

const DEPLOY_STATE_KEY = 'htx-mock-app-deploy-states';

let deployStateOverrides: Map<string, boolean> = new Map();

function loadDeployStates(): void {
  const stored = localStorage.getItem(DEPLOY_STATE_KEY);
  if (stored) {
    deployStateOverrides = new Map(JSON.parse(stored));
  }
}

function persistDeployStates(): void {
  localStorage.setItem(DEPLOY_STATE_KEY, JSON.stringify([...deployStateOverrides]));
}

loadDeployStates();

const ALL_USE_CASE_APPS: Array<'supply-chain' | 'shortage-management'> =
  ['supply-chain', 'shortage-management'];

export function getMockedActiveUseCases(): UseCaseApp[] {
  const mode = getCurrentAppManagementMockMode();
  if (mode === 'no-use-cases') return [];
  return ALL_USE_CASE_APPS.filter((app) => deployStateOverrides.get(app) === true);
}

export function getMockedApprovedUseCases(): UseCaseApp[] {
  const mode = getCurrentAppManagementMockMode();
  if (mode === 'no-use-cases') return [];
  return [...ALL_USE_CASE_APPS];
}

const APP_MOCK_URLS: Record<'supply-chain' | 'shortage-management', string> = {
  'supply-chain': 'https://mock-tenant-docapp.example.com/',
  'shortage-management': 'https://mock-tenant-lep-report-app.example.com/'
};

export function getMockedApps(): ManagedApp[] {
  const mode = getCurrentAppManagementMockMode();
  if (mode === 'no-use-cases') return [];
  const active = new Set(getMockedActiveUseCases());
  const approved = new Set(getMockedApprovedUseCases());
  const apps: ManagedApp[] = ALL_USE_CASE_APPS.map((app) => ({
    id: app,
    ...USE_CASE_APP_METADATA[app],
    deploymentState: (active.has(app) ? 'DEPLOYED' : 'NOT_DEPLOYED') as DeploymentState,
    appUrl: APP_MOCK_URLS[app],
    online: active.has(app)
  }));

  // Integrator is a standalone app visible when supply-chain is approved.
  // It has its own independent install state tracked in deployStateOverrides.
  if (approved.has('supply-chain')) {
    const integratorDeployed = deployStateOverrides.get('integrator') === true;
    apps.push({
      id: 'integrator',
      ...USE_CASE_APP_METADATA['integrator'],
      deploymentState: integratorDeployed ? 'DEPLOYED' : 'NOT_DEPLOYED',
      appUrl: null,
      online: integratorDeployed
    });
  }

  if (approved.has('shortage-management')) {
    const lepappDeployed = deployStateOverrides.get('lepapp') === true;
    apps.push({
      id: 'lepapp',
      ...USE_CASE_APP_METADATA['lepapp'],
      deploymentState: lepappDeployed ? 'DEPLOYED' : 'NOT_DEPLOYED',
      appUrl: 'https://mock-bfarm-lepapp.example.com/',
      online: lepappDeployed
    });
  }

  return apps;
}

export function getMockedDeploymentStatus(): TenantStatus {
  return {
    organizationId: 'mock-org-id',
    tenantName: 'mock-tenant',
    state: 'ACTIVE',
    createdDate: new Date().toISOString(),
    modifiedDate: new Date().toISOString(),
    managementUrl: 'https://mock-connector-management.example.com',
    syncStatus: 'Synced',
    healthStatus: 'Healthy',
    lastSyncFinishedAt: new Date(Date.now() - 300_000).toISOString()
  };
}

export function enableMockedUseCase(useCase: UseCaseApp): void {
  deployStateOverrides.set(useCase, true);
  persistDeployStates();
}

export function disableMockedUseCase(useCase: UseCaseApp): void {
  deployStateOverrides.set(useCase, false);
  persistDeployStates();
}
